"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiEllipsisVertical, HiOutlineArrowTopRightOnSquare, HiOutlineCog6Tooth, HiOutlineFunnel, HiOutlineLockClosed, HiOutlineLockOpen, HiOutlinePlus, HiOutlineRectangleGroup, HiXMark } from "react-icons/hi2";
import { RiCheckLine, RiDeleteBinLine, RiUser3Line } from "react-icons/ri";
import { CreateTaskModal } from "@/app/[tenant]/(console)/tasks/modals/create-task-modal";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import { ProgressBarLink } from "@/components/progress-bar";
import { Button } from "@/components/ui";
import { type BulkAction, BulkActionsBar } from "@/components/ui/bulk-actions-bar";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSubMenu, DropdownMenuSubMenuContent, DropdownMenuSubMenuTrigger, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { Skeleton } from "@/components/ui/skeleton";
import { useUser } from "@/contexts/user-provider";
import { useCanAssignTasks, useCanWriteTasks } from "@/hooks/use-can-write-tasks";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { useEffectiveTaskScope } from "@/hooks/use-effective-task-scope";
import { CalendarDay, daysBetween } from "@/lib/clinic-clock";
import { formatDate, formatName, formatStaffName, formatToHumanReadable } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { TaskDefaultFilter, TaskStatus, TaskSummary } from "@/types/task";
import { useTaskSettings } from "@/api/provider/tasks/task-settings-query";
import { useTaskMutations, useTasksInfinite } from "@/api/provider/tasks/tasks-query";
import { selectTaskSidebar, taskSidebarDefinition } from "@/features/sidebar/task-sidebar";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
import { TaskGroupBy, TaskPanelAssigneeFilter, useTasksDrawerStore } from "@/stores/tasks-drawer-store";
const ASSIGNEE_FILTER_LABELS: Record<TaskPanelAssigneeFilter, string> = {
  assigned: "Assigned to me",
  subscribed: "Subscribed to me",
  all: "All tasks"
};
const GROUP_BY_LABELS: Record<TaskGroupBy, string> = {
  priority: "Urgency",
  dueDate: "Due date",
  assignee: "Assignee",
  status: "Status"
};

/**
 * Server sort that makes each group arrive as one contiguous run.
 *
 * The list is paginated, so grouping can only ever chunk what the server
 * already ordered. If the sort and the grouping disagree, page two drops rows
 * into a group above where you are reading and the list jumps under you.
 */
const GROUP_BY_SORT: Record<TaskGroupBy, {
  sortBy: string;
  sortDirection: "asc" | "desc";
}> = {
  priority: {
    sortBy: "priority",
    sortDirection: "desc"
  },
  dueDate: {
    sortBy: "due_date",
    sortDirection: "asc"
  },
  assignee: {
    sortBy: "assignee_id",
    sortDirection: "asc"
  },
  status: {
    sortBy: "status",
    sortDirection: "desc"
  }
};
function dueBucket(dueDate: CalendarDay | null | undefined, today: CalendarDay): string {
  if (!dueDate) return "No due date";
  const days = daysBetween(today, dueDate);
  if (days < 0) return "Overdue";
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days <= 7) return "This week";
  return "Later";
}

/** Who a task is on. A task carries either an assignee or a team, never both. */
function assigneeLabel(task: TaskSummary): string {
  if (task.assignee) {
    return formatStaffName(task.assignee);
  }
  return task.team?.name ?? "Unassigned";
}

/** Nobody is on this task — neither a person nor a team. */
function isUnassigned(task: TaskSummary): boolean {
  return !task.assignee && !task.team;
}

/** Past its due day on the viewer's clinic calendar and still open. */
function isOverdue(task: TaskSummary, today: CalendarDay): boolean {
  if (!task.dueDate) return false;
  if (task.status === TaskStatus.COMPLETED || task.status === TaskStatus.CANCELLED) {
    return false;
  }
  return task.dueDate < today;
}
function groupKeyFor(task: TaskSummary, groupBy: TaskGroupBy, today: CalendarDay): string {
  switch (groupBy) {
    case "dueDate":
      return dueBucket(task.dueDate, today);
    case "assignee":
      // Safe to use the row's own label because the server sorts by
      // COALESCE(assignee_id, team_id) — each owner, person or team, arrives as
      // one contiguous run. Grouping has to follow the sort or a run breaks
      // into repeated, interleaved headers.
      return assigneeLabel(task);
    case "status":
      return formatToHumanReadable(task.status);
    case "priority":
    default:
      return formatToHumanReadable(task.priority);
  }
}

/**
 * Chunk the list into consecutive runs of the same group key.
 *
 * Deliberately does not sort — not the groups, not the rows inside them. The
 * server has already ordered the page; re-sorting here would reintroduce exactly the
 * jumping this is meant to avoid, because a later page cannot know what an
 * earlier one already rendered.
 */
function groupTasks(tasks: TaskSummary[], groupBy: TaskGroupBy, today: CalendarDay): {
  key: string;
  tasks: TaskSummary[];
}[] {
  const groups: {
    key: string;
    tasks: TaskSummary[];
  }[] = [];
  for (const task of tasks) {
    const key = groupKeyFor(task, groupBy, today);
    const current = groups[groups.length - 1];
    if (current && current.key === key) current.tasks.push(task);else groups.push({
      key,
      tasks: [task]
    });
  }
  return groups;
}

/**
 * Loading state that mirrors the real list: group header, then rows with a
 * checkbox, a title and a meta line. Matching the shape means nothing jumps
 * when the data lands.
 */
function TasksPanelSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden={true}>{[4, 3].map(_temp2)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp2(rowCount, groupIndex) {
  return <section key={groupIndex}><div className="flex items-center gap-2 border-b border-border bg-background-secondary px-3 py-1.5"><Skeleton className="h-3 w-20" /></div>{Array.from({
      length: rowCount
    }).map(_temp)}</section>;
}
function _temp(_, rowIndex) {
  return <div key={rowIndex} className="flex items-start gap-2.5 border-b border-border px-3 py-2.5"><Skeleton className="mt-0.5 size-4 shrink-0 rounded" /><div className="min-w-0 flex-1 space-y-1.5"><Skeleton className="h-3.5 w-full" />{rowIndex % 2 === 0 && <Skeleton className="h-3.5 w-3/5" />}<Skeleton className="h-3 w-2/5" /></div></div>;
}
interface TasksPanelProps {
  className?: string;
  mode?: "desktop" | "mobile";
  onClose?: () => void;
}
export function TasksPanel({
  className,
  mode = "desktop",
  onClose
}: TasksPanelProps) {
  const {
    currentUser
  } = useUser();
  const today = useClinicClock().today();
  const {
    isLocked,
    lockDrawer: lockTasksPanel,
    unlockDrawer: unlockTasksPanel,
    closeDrawer: closeTasksPanel,
    context,
    clearContext,
    panelFilters,
    setPanelFilters
  } = useTasksDrawerStore();
  const openSidebar = useRightSidebarStore(s => s.open);
  const openTaskId = useRightSidebarStore(s_0 => selectTaskSidebar(s_0).taskId);
  const closeSidebar = useRightSidebarStore(s_1 => s_1.close);
  const {
    filterStatus,
    assigneeFilter,
    groupBy
  } = panelFilters;
  const setGroupBy = (next: TaskGroupBy) => setPanelFilters({
    groupBy: next
  });
  const {
    data: taskSettings
  } = useTaskSettings();
  const clinicFilter = taskSettings?.defaultTaskFilter ?? TaskDefaultFilter.USER_CHOICE;
  const isUserChoice = clinicFilter === TaskDefaultFilter.USER_CHOICE;

  // Shared with the nav badge so the list and the number agree.
  const effectiveAssigneeFilter = useEffectiveTaskScope();
  const [editingTask, setEditingTask] = useState<TaskSummary | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const {
    complete,
    update,
    delete: deleteTask
  } = useTaskMutations();
  const canWriteTasks = useCanWriteTasks();
  const canAssignTasks = useCanAssignTasks();
  // Selecting is only worth offering if something can be done with a selection.
  // An assign-only user is here for the case the permission exists for: the
  // manager is out and the front desk takes their whole queue in one go.
  const canSelectTasks = canWriteTasks || canAssignTasks;
  const [isBulkRunning, setIsBulkRunning] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  // The bulk bar is portalled to the body, which is only reachable after mount.
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);
  const taskPageHref = context?.type === "call" ? `/tasks?callId=${encodeURIComponent(context.callId)}` : "/tasks";
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Grouping is the panel's ordering control: it picks the server sort so each
  // group arrives whole, rather than being a second, conflicting knob.
  // A groupBy read back from localStorage may name an option that no longer
  // exists, which would index to undefined and take the query with it.
  const {
    sortBy,
    sortDirection
  } = GROUP_BY_SORT[groupBy] ?? GROUP_BY_SORT.priority;
  const statusFilter = filterStatus === "ALL" ? undefined : filterStatus === "ACTIVE" ? [TaskStatus.OPEN, TaskStatus.IN_PROGRESS] : [filterStatus];
  const {
    tasks: fetchedTasks,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useTasksInfinite({
    sortBy,
    sortDirection,
    status: statusFilter,
    assigneeId: effectiveAssigneeFilter === "assigned" ? currentUser?.id : undefined,
    subscriberId: effectiveAssigneeFilter === "subscribed" ? currentUser?.id : undefined,
    callId: context?.type === "call" ? context.callId : undefined
  });

  /**
   * Honour the status filter locally as well as on the server.
   *
   * Completion updates the cache optimistically, but the row only leaves the
   * list once something drops it. Waiting for the refetch means a task you
   * just finished lingers for as long as the request takes, which reads as the
   * click not having worked.
   */
  const tasks = useMemo(() => {
    if (filterStatus === "ALL") return fetchedTasks;
    if (filterStatus === "ACTIVE") {
      return fetchedTasks.filter(task => task.status !== TaskStatus.COMPLETED && task.status !== TaskStatus.CANCELLED);
    }
    return fetchedTasks.filter(task_0 => task_0.status === filterStatus);
  }, [fetchedTasks, filterStatus]);
  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = loadMoreRef.current;
    if (!root || !sentinel) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, {
      root,
      rootMargin: "120px",
      threshold: 0
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, tasks.length]);
  const handleClose = () => {
    if (onClose) onClose();else closeTasksPanel();
  };

  /**
   * Advance the open task when it drops out of the queue.
   *
   * Completion happens from several places — the row action here, the big
   * "Mark done" in the detail pane, the kanban board — and only the panel
   * knows what comes next. Watching the list covers all of them, where
   * advancing inside the row handler only covered one.
   */
  const orderedIdsRef = useRef<string[]>([]);
  // Ids a bulk action just acted on. Several rows leaving at once is exactly
  // what the guard below treats as suspicious, so a bulk delete has to say
  // that it meant it — otherwise the sidebar keeps an open task that no
  // longer exists and shows its loading skeleton forever.
  const bulkAffectedIdsRef = useRef<string[]>([]);
  useEffect(() => {
    const previousIds = orderedIdsRef.current;
    const currentIds = tasks.map(task_1 => task_1.id);
    orderedIdsRef.current = currentIds;
    if (isLoading || !openTaskId) return;
    const currentIdSet = new Set(currentIds);
    if (currentIdSet.has(openTaskId)) return;
    const previousIndex = previousIds.indexOf(openTaskId);
    if (previousIndex === -1) return;
    // Every other loaded task is still loaded, so this was a single removal —
    // a completion — rather than a filter or scope change rewriting the list,
    // where jumping to an unrelated task would be worse than staying put.
    // Deliberately not a length comparison: the list is offset-paginated and a
    // refetch backfills the freed slot from the next page, leaving the count
    // unchanged, which silently disabled this in exactly the busy queues it
    // exists for.
    const bulkAffected = new Set(bulkAffectedIdsRef.current);
    const onlyOpenTaskLeft = previousIds.every(id => id === openTaskId || currentIdSet.has(id) || bulkAffected.has(id));
    if (!onlyOpenTaskLeft) return;
    bulkAffectedIdsRef.current = [];
    const next_0 = tasks[previousIndex] ?? tasks[previousIndex - 1];
    if (next_0) openSidebar(taskSidebarDefinition, {
      taskId: next_0.id
    });else closeSidebar(taskSidebarDefinition.id);
  }, [tasks, openTaskId, isLoading, openSidebar, closeSidebar]);

  // Selection is scoped to what is on screen. Leaving it in place across a
  // filter, scope, status or grouping change would let a bulk action hit tasks
  // the user can no longer see and never meant to include — grouping counts
  // because it picks the server sort, so it changes which page you are on.
  useEffect(() => {
    setSelectedIds(prev => prev.size === 0 ? prev : new Set());
  }, [effectiveAssigneeFilter, filterStatus, groupBy, context?.callId]);
  const toggleSelected = (taskId: string) => {
    setSelectedIds(prev_0 => {
      const next_1 = new Set(prev_0);
      if (next_1.has(taskId)) next_1.delete(taskId);else next_1.add(taskId);
      return next_1;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  /**
   * Completing from the row keeps the queue in place and moves the open task
   * sidebar on to the next one, so a run of acknowledgements never costs a
   * re-open. No bulk endpoint exists for tasks, so bulk actions fan out over
   * the per-task mutations the same way the services page does.
   */
  const completeTask = async (task_2: TaskSummary) => {
    try {
      await complete.mutateAsync(task_2.id);
      setSelectedIds(prev_1 => {
        if (!prev_1.has(task_2.id)) return prev_1;
        const remaining = new Set(prev_1);
        remaining.delete(task_2.id);
        return remaining;
      });
      toast({
        variant: "success",
        title: "Task marked done"
      });
    } catch {
      toast({
        variant: "error",
        title: "Failed to complete task"
      });
    }
  };
  const runBulk = async (label: string, action: (taskId: string) => Promise<unknown>) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    bulkAffectedIdsRef.current = ids;
    setIsBulkRunning(true);
    const results = await Promise.allSettled(ids.map(action));
    const failed = results.filter(r => r.status === "rejected").length;
    setIsBulkRunning(false);
    clearSelection();
    if (failed === 0) {
      toast({
        variant: "success",
        title: `${ids.length} ${ids.length === 1 ? "task" : "tasks"} ${label}`
      });
      return;
    }
    toast({
      variant: "error",
      title: `${failed} of ${ids.length} could not be ${label}`
    });
  };

  // Each action carries the permission its request needs, so the bar offers an
  // assign-only user the one the API will take and none of the ones it would
  // refuse. "Assign to me" sends nothing but an assignee, which is what the
  // update route gates on tasks:assign; marking done is a status change and
  // deleting is a delete, so both stay on tasks:write.
  const bulkActions: BulkAction[] = [{
    allowed: canWriteTasks,
    action: {
      id: "complete",
      label: "Mark done",
      icon: <RiCheckLine />,
      onClick: () => runBulk("marked done", id_0 => complete.mutateAsync(id_0))
    }
  }, {
    allowed: canAssignTasks,
    action: {
      id: "assign",
      label: "Assign to me",
      icon: <RiUser3Line />,
      disabled: !currentUser?.id,
      onClick: () => runBulk("assigned", id_1 => update.mutateAsync({
        taskId: id_1,
        task: {
          assigneeId: currentUser?.id,
          teamId: null
        }
      }))
    }
  }, {
    allowed: canWriteTasks,
    action: {
      id: "delete",
      label: "Delete",
      variant: "destructive" as const,
      icon: <RiDeleteBinLine />,
      // Destructive and irreversible across a whole selection — confirm first,
      // the same way deleting a single task does.
      onClick: () => setShowBulkDeleteConfirm(true)
    }
  }].filter(entry => entry.allowed).map(entry_0 => entry_0.action);
  return <div className={cx("flex h-full flex-col", className)}>
      {/* Header */}
      <div className="flex h-10 items-center gap-1 border-b border-border px-3 shadow-md md:shadow-none">
        <h2 className="min-w-0 flex-1 truncate text-body font-semibold text-foreground">
          {context?.type === "call" ? "Call Tasks" : "Tasks"}
        </h2>
        <div className="flex shrink-0 items-center gap-0.5">
          {canWriteTasks && <Button variant="ghost" className="text-foreground-secondary hover:text-foreground !shadow-none size-9" onClick={() => setIsCreateOpen(true)} tooltip="New task" aria-label="New task" icon={HiOutlinePlus} />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-foreground-secondary hover:text-foreground !shadow-none size-9" tooltip="Filter & Sort" icon={HiOutlineFunnel} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isUserChoice && <>
                  <DropdownMenuLabel>Show</DropdownMenuLabel>
                  {(Object.keys(ASSIGNEE_FILTER_LABELS) as TaskPanelAssigneeFilter[]).map(option => <DropdownMenuItem key={option} onClick={() => setPanelFilters({
                assigneeFilter: option
              })} className={cx(effectiveAssigneeFilter === option && "bg-hover")}>
                      {ASSIGNEE_FILTER_LABELS[option]}
                    </DropdownMenuItem>)}
                  <DropdownMenuSeparator />
                </>}
              <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setPanelFilters({
              filterStatus: "ACTIVE"
            })} className={cx(filterStatus === "ACTIVE" && "bg-hover")}>
                Active
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPanelFilters({
              filterStatus: "ALL"
            })} className={cx(filterStatus === "ALL" && "bg-hover")}>
                All Statuses
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPanelFilters({
              filterStatus: TaskStatus.OPEN
            })} className={cx(filterStatus === TaskStatus.OPEN && "bg-hover")}>
                Open
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPanelFilters({
              filterStatus: TaskStatus.IN_PROGRESS
            })} className={cx(filterStatus === TaskStatus.IN_PROGRESS && "bg-hover")}>
                In Progress
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPanelFilters({
              filterStatus: TaskStatus.COMPLETED
            })} className={cx(filterStatus === TaskStatus.COMPLETED && "bg-hover")}>
                Completed
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPanelFilters({
              filterStatus: TaskStatus.CANCELLED
            })} className={cx(filterStatus === TaskStatus.CANCELLED && "bg-hover")}>
                Cancelled
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuSubMenu>
                <DropdownMenuSubMenuTrigger>
                  <HiOutlineRectangleGroup className="mr-2 h-4 w-4" />
                  <span>Group by</span>
                </DropdownMenuSubMenuTrigger>
                <DropdownMenuSubMenuContent>
                  {(Object.keys(GROUP_BY_LABELS) as TaskGroupBy[]).map(option_0 => <DropdownMenuItem key={option_0} onClick={() => setGroupBy(option_0)} className={cx(groupBy === option_0 && "bg-hover")}>
                        {GROUP_BY_LABELS[option_0]}
                      </DropdownMenuItem>)}
                </DropdownMenuSubMenuContent>
              </DropdownMenuSubMenu>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-foreground-secondary hover:text-foreground !shadow-none size-9" tooltip="More" aria-label="More panel options" icon={HiEllipsisVertical} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {context?.type === "call" && <DropdownMenuItem onClick={clearContext}>
                  <HiXMark className="mr-2 h-4 w-4" />
                  Show all tasks
                </DropdownMenuItem>}
              {mode === "desktop" && <DropdownMenuItem onClick={isLocked ? unlockTasksPanel : lockTasksPanel}>
                  {isLocked ? <HiOutlineLockClosed className="mr-2 h-4 w-4" /> : <HiOutlineLockOpen className="mr-2 h-4 w-4" />}
                  <span>{isLocked ? "Unpin panel" : "Keep panel open"}</span>
                </DropdownMenuItem>}
              <ProgressBarLink href={taskPageHref} onClick={handleClose} className="w-full block">
                <DropdownMenuItem>
                  <HiOutlineArrowTopRightOnSquare className="mr-2 h-4 w-4" />
                  Open task page
                </DropdownMenuItem>
              </ProgressBarLink>
              <DropdownMenuSeparator />
              <ProgressBarLink href="/settings/tasks" onClick={handleClose} className="w-full block">
                <DropdownMenuItem>
                  <HiOutlineCog6Tooth className="mr-2 h-4 w-4" />
                  Task settings
                </DropdownMenuItem>
              </ProgressBarLink>
            </DropdownMenuContent>
          </DropdownMenu>
          {mode === "mobile" && onClose && <Button variant="ghost" className="h-7 w-7 p-0 text-foreground-secondary hover:text-foreground" onClick={onClose} tooltip="Close" icon={HiXMark} />}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? <TasksPanelSkeleton /> : tasks.length === 0 ? <div className="flex h-32 flex-col items-center justify-center text-center">
            <p className="text-body font-medium text-foreground">
              No tasks found
            </p>
            <p className="text-caption text-foreground-secondary">
              Try changing your filters.
            </p>
          </div> : <>
            {groupTasks(tasks, groupBy, today).map((group, groupIndex) => <section key={`${group.key}-${groupIndex}`}>
                <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-background-secondary px-3 py-1.5">
                  <span className="text-caption-strong uppercase tracking-wide text-foreground-secondary">
                    {group.key}
                  </span>
                  <span className="text-caption text-foreground-muted">
                    {group.tasks.length}
                  </span>
                </header>
                {group.tasks.map(task_3 => {
            const isSelected = selectedIds.has(task_3.id);
            return <div key={task_3.id} className={cx("group flex items-start gap-2.5 border-b border-border px-3 py-2.5 transition-colors hover:bg-row-hover", (isSelected || openTaskId === task_3.id) && "bg-row-hover")}>
                      {canSelectTasks && <Checkbox checked={isSelected} onCheckedChange={() => toggleSelected(task_3.id)} aria-label={`Select ${task_3.title}`} className="mt-0.5 shrink-0" />}
                      <button onClick={() => openSidebar(taskSidebarDefinition, {
                taskId: task_3.id
              })} className="min-w-0 flex-1 text-left">
                        <p className="line-clamp-2 text-body font-medium text-foreground">
                          {task_3.title}
                        </p>
                        {task_3.patient && <p className="mt-1 truncate text-caption font-medium text-foreground">
                            {formatName(task_3.patient.firstName, task_3.patient.lastName)}
                          </p>}
                        <p className="mt-1 truncate text-caption text-foreground-secondary">
                          {/* Warnings as emphasis on the line that is already
                              there, rather than chips — nobody is watching a
                              task that has neither an owner nor a live date. */}
                          <span className={cx(isUnassigned(task_3) && "font-medium text-status-warning-text")}>
                            {assigneeLabel(task_3)}
                          </span>
                          <span className={cx(isOverdue(task_3, today) && "font-medium text-status-error-text")}>
                            {task_3.dueDate ? ` · ${isOverdue(task_3, today) ? "Overdue " : ""}${formatDate(task_3.dueDate)}` : ""}
                          </span>
                        </p>
                      </button>
                      {canWriteTasks && task_3.status !== TaskStatus.COMPLETED && task_3.status !== TaskStatus.CANCELLED && <Button variant="ghost" className={cx("size-7 shrink-0 !shadow-none text-foreground-secondary transition-opacity hover:text-status-success-text focus-visible:opacity-100 group-hover:opacity-100",
              // Hover reveal has no equivalent on touch, so the
              // mobile panel shows it permanently.
              mode === "mobile" ? "opacity-100" : "opacity-0")} onClick={() => completeTask(task_3)} tooltip="Mark done" aria-label={`Mark ${task_3.title} done`} icon={RiCheckLine} />}
                    </div>;
          })}
              </section>)}
            {hasNextPage && <div ref={loadMoreRef} className="flex min-h-8 items-center justify-center py-2 text-caption text-foreground-secondary">
                {isFetchingNextPage ? "Loading more…" : null}
              </div>}
          </>}
      </div>

      {/* Portalled to the body on purpose. The panel's container in
          `sidebar-layout` carries both a CSS transform and `overflow-hidden`:
          the transform makes `position: fixed` resolve against that element
          rather than the viewport, and the overflow then clips it. Rendered in
          place, the bar is trapped at the panel's 320px and cut off. */}
      {isMounted && bulkActions.length > 0 && createPortal(<BulkActionsBar selectedCount={selectedIds.size} itemLabel="task" actions={bulkActions} onClear={clearSelection} isLoading={isBulkRunning} position="fixed" ariaLabel="Task bulk actions" />, document.body)}

      <DeleteConfirmationModal isOpen={showBulkDeleteConfirm} setIsOpen={setShowBulkDeleteConfirm} onConfirm={() => runBulk("deleted", id_2 => deleteTask.mutateAsync(id_2))} title={`Delete ${selectedIds.size} ${selectedIds.size === 1 ? "task" : "tasks"}`} subtitle="Are you sure? This can't be undone." confirmText="Delete" isLoading={isBulkRunning} />

      <CreateTaskModal task={editingTask ?? undefined} open={isCreateOpen || !!editingTask} onOpenChange={open => {
      if (open) return;
      setIsCreateOpen(false);
      setEditingTask(null);
    }} />
    </div>;
}